import { useState, useEffect, useCallback } from 'react';
import { styled } from '@mui/material/styles';
import { useAppDispatch, useAppSelector } from '../../store/hooks';

import { ControlForm, ControlTextures, ControlSelection, ColorSelection, layerPeep, type LayerType } from './Controls';
import DialActions from './DialActions';
import ColorPicker from './ColorPicker';
import Canvas from '../../components/layout/Canvas';
import { RGBtoHex } from './helpers';
import assets from '../../consume';
import { setToaster } from '../../common/commonSlice';
import { postImage, setPeeps, setPeep } from './drawSlice';
import type { PeepLayer, Peeps } from '../../types';

// Skin color combinations
const skins = [
  ["#ECCCA2", "#C6AB88"],
  ["#DAC49E", "#AF9D7E"],
  ["#CAA77C", "#B5966F"],
  ["#AD8B62", "#806748"],
  ["#3C332B", "#302C28"]
];

// Variant mappings
const variant: Record<string, string[]> = {
  hair: ['hair_default', 'hair_hats', 'hair_specials'],
  wear: ['wear_default', 'wear_brands', 'wear_casuals', 'wear_formals', 'wear_specials', 'wear_sports'],
  addon: ['addon_default', 'addon_flags', 'addon_masks', 'addon_stuffs']
};

// Styled components
const SmallScreenText = styled('p')({
  color: '#54a936',
  fontWeight: 800,
  textAlign: 'center',
  border: '1px solid hsla(230,8%,60%,.8)',
  borderRadius: 10,
  margin: '-20px 10px 0',
  whiteSpace: 'pre-line',
  padding: '10px 5px'
});

export default function Draw() {
  const dispatch = useAppDispatch();
  const width = window.innerWidth;

  // Redux state
  const canvas = useAppSelector((state) => state.draw.canvas);
  const peeps = useAppSelector((state) => state.draw.peeps);
  const gender = useAppSelector((state) => state.draw.gender);
  const looking = useAppSelector((state) => state.draw.looking);
  const skin = useAppSelector((state) => state.draw.skin);
  const hair = useAppSelector((state) => state.draw.hair);
  const wear = useAppSelector((state) => state.draw.wear);
  const mouth = useAppSelector((state) => state.draw.mouth);
  const addon = useAppSelector((state) => state.draw.addon);

  // Local state
  const [open, setOpen] = useState(false);
  const [context, setContext] = useState<CanvasRenderingContext2D | null>(null);
  const [selectedColor, setSelectedColor] = useState('#000000');
  const [assetColorIndex, setAssetColorIndex] = useState<number | null>(null);
  const [activeLayer, setActiveLayer] = useState<LayerType>('skin');

  // Canvas rendering functions - keeping original logic intact
  const clear = useCallback(() => {
    if (context !== null && canvas) {
      context.clearRect(0, 0, canvas.width, canvas.height);
    }
  }, [context, canvas]);

  const drawCanvas = useCallback((M?: unknown) => {
    if (!context || !canvas) return;

    if (!M) {
      Object.keys(peeps).sort().forEach((p) => drawCanvas(peeps[p as keyof typeof peeps]));
    } else if (Array.isArray(M) && typeof M[0] !== "string") {
      for (const m in M) drawCanvas(M[m]);
    } else if (Array.isArray(M)) {
      const color = M[0] as string;
      const pixels = M[1] as Record<string, number | number[]>;

      for (const m in pixels) {
        let pixelWidth = 7, pixelHeight = 7;
        let Mm: number;
        const pixelData = pixels[m];

        if (Array.isArray(pixelData)) {
          Mm = pixelData[0];
          pixelWidth = pixelData[1] * 7;
          if (pixelData[2]) pixelHeight = pixelData[2] * 7;
        } else {
          Mm = pixelData;
        }

        let posy = Mm / 43;
        const pyrn = Math.round(posy);
        let posx = Mm - (43 * pyrn);
        posx = ((posx < 0) ? 43 + posx : posx) * 7;
        posy = (pyrn - ((posy < pyrn) ? 1 : 0)) * 7;

        if (looking) {
          posx = canvas.width - posx;
          pixelWidth *= -1;
        }

        context.fillStyle = color || "#FF0000";
        context.fillRect(posx, posy, pixelWidth, pixelHeight);
      }
    }
  }, [context, canvas, peeps, looking]);

  const redraw = useCallback(() => {
    if (context !== null) {
      clear();
      drawCanvas();
    }
  }, [context, clear, drawCanvas]);

  // Save avatar as PNG
  const save = () => {
    const link = document.createElement('a');
    link.download = '8bitpix.png';
    const canvasElement = document.getElementById('canvas') as HTMLCanvasElement;
    link.href = canvasElement.toDataURL('image/png');
    link.click();
  };

  // Share to Twitter via Imgur
  const share = () => {
    const canvasElement = document.getElementById('canvas') as HTMLCanvasElement;
    const img = canvasElement.toDataURL('image/png').split(',')[1];
    const multiPartForm = new FormData();
    multiPartForm.append('image', img);
    multiPartForm.append('title', localStorage.getItem('user_code') + '_bitpix');
    multiPartForm.append('description', 'Bitpix #pixelart avatar generated with 8bitpix app https://8bitpix.com');

    dispatch(postImage(multiPartForm))
      .unwrap()
      .then((res) => {
        if (res.success) {
          const url = `https://twitter.com/intent/tweet?hashtags=pixelart&text=Hey%21%20checkout%20my%20new%20avatar%21%20I%20made%20it%20with%208bitpix%20app%20imgur.com%2F${res.data.id}%20via%20%408bitpixapp`;
          window.open(url, '_blank');
        }
      })
      .catch((err) => console.error('Error sharing:', err));
  };

  // Helper to get valid asset - cast to PeepLayer[] for type safety
  const validAsset = (asset: string | null, index: number, type: string | null = null): PeepLayer[] => {
    const Json = assets as Record<string, PeepLayer[][][]>;
    if (type) {
      const part = Json[type]?.[gender]?.[index];
      return part ? part : Json[type]?.[gender]?.[0] || [];
    }
    const part = Json[asset!]?.[gender]?.[index];
    return part ? part : Json[asset!]?.[gender]?.[0] || [];
  };

  // Handle right-click to pick color
  const onContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!context) return;

    const imageData = context.getImageData(e.nativeEvent.offsetX, e.nativeEvent.offsetY, 1, 1);
    const { data } = imageData;
    const color = ('#' + RGBtoHex(data[0], data[1], data[2])).toUpperCase();
    const peepLayer = peeps[layerPeep[activeLayer] as keyof typeof peeps];
    const index = peepLayer.findIndex((p) => (p[0] as string).toUpperCase() === color);

    if (index > -1) {
      setSelectedColor(color);
      setAssetColorIndex(index);
      setOpen(true);
    } else {
      dispatch(setToaster({
        open: true,
        message: 'To change a piece color, it must be selected',
        severity: 'info'
      }));
    }
  };

  // Handle color change from picker
  const onColorChange = (newColor: string) => {
    setSelectedColor(newColor);
    const layerKey = layerPeep[activeLayer] as keyof typeof peeps;
    const newPeep = [...peeps[layerKey]];
    if (assetColorIndex !== null && newPeep[assetColorIndex]) {
      newPeep[assetColorIndex] = [newColor.toUpperCase(), newPeep[assetColorIndex][1]];
      dispatch(setPeep({ name: layerKey, data: newPeep }));
    }
  };

  // Initialize canvas context
  useEffect(() => {
    if (canvas !== null && context === null) {
      setContext(canvas.getContext('2d'));
    }
  }, [canvas, context]);

  // Initialize peeps when context or gender/looking changes
  useEffect(() => {
    if (context !== null) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const Json = assets as Record<string, any>;
      const genSkin: PeepLayer[] = [
        [skins[skin.index][0], Json.gender[gender][0][1]],
        [skins[skin.index][1], Json.gender[gender][1][1]]
      ];

      dispatch(setPeeps({
        "1gen": genSkin,
        "4mouth": validAsset(null, mouth.index, 'mouth'),
        "3hair": validAsset(variant.hair[hair.variant], hair.index),
        "2wear": validAsset(variant.wear[wear.variant], wear.index),
        "5addon": validAsset(variant.addon[addon.variant], addon.index),
      } as Peeps));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gender, looking, context]);

  // Update skin when skin index changes
  useEffect(() => {
    if (context !== null) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const Json = assets as Record<string, any>;
      const genSkin: PeepLayer[] = [
        [skins[skin.index][0], Json.gender[gender][0][1]],
        [skins[skin.index][1], Json.gender[gender][1][1]]
      ];
      dispatch(setPeep({ name: "1gen", data: genSkin }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skin]);

  // Update mouth when mouth index changes
  useEffect(() => {
    if (context !== null) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const Json = assets as Record<string, any>;
      dispatch(setPeep({ name: "4mouth", data: Json.mouth[gender][mouth.index] }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mouth]);

  // Update hair when hair index/variant changes
  useEffect(() => {
    if (context !== null) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const Json = assets as Record<string, any>;
      dispatch(setPeep({ name: "3hair", data: Json[variant.hair[hair.variant]][gender][hair.index] }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hair]);

  // Update wear when wear index/variant changes
  useEffect(() => {
    if (context !== null) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const Json = assets as Record<string, any>;
      dispatch(setPeep({ name: "2wear", data: Json[variant.wear[wear.variant]][gender][wear.index] }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wear]);

  // Update addon when addon index/variant changes
  useEffect(() => {
    if (context !== null) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const Json = assets as Record<string, any>;
      dispatch(setPeep({ name: "5addon", data: Json[variant.addon[addon.variant]][gender][addon.index] }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addon]);

  // Redraw canvas when peeps change
  useEffect(() => {
    if (context !== null) {
      redraw();
    }
  }, [context, peeps, redraw]);

  // Render small screen message
  if (width <= 310) {
    return (
      <SmallScreenText>
        Unfortunately your device is too small to support 8bitpix.
        <br /><br />
        Try on desktop or using a larger screen.
      </SmallScreenText>
    );
  }

  return (
    <>
      <div className="container">
        <div
          className="maker"
          style={{ position: 'relative', zIndex: 100 }}
          onContextMenu={onContextMenu}
        >
          <Canvas id="canvas" />
        </div>
      </div>
      <ControlForm />
      <ControlTextures layer={activeLayer} setLayer={setActiveLayer} />
      <ControlSelection
        layer={activeLayer}
        variant={variant[activeLayer]}
        skins={skins}
      />
      {peeps && (
        <ColorSelection
          layer={activeLayer}
          setColor={setSelectedColor}
          setIndex={setAssetColorIndex}
          setOpen={setOpen}
        />
      )}
      <DialActions save={save} share={share} />
      <ColorPicker
        open={open}
        color={selectedColor}
        setIndex={setAssetColorIndex}
        onColorChange={onColorChange}
        onClose={() => setOpen(false)}
      />
    </>
  );
}
